import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Clock, Send, CheckCircle, Headphones } from 'lucide-react';
import { COMPANY_INFO, ANIMATION_VARIANTS, HERO_CONTENT } from '../constants';
import { 
  CSSContainment, 
  usePerformanceMonitoring 
} from '../utils/performance';
import { 
  useFloatingParticles,
  useOptimizedBackground
} from '../hooks/useAnimations';
import { GlassCard, StyledIcon } from '../components/animations';
import { handlePageNavigation } from '../utils/navigation';
import { contactApiService } from '../services/contactApi';

interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  company: string;
  jobTitle: string;
  subject: string;
  enquiryType: string;
  gpuTypes: string[];
  message: string;
  agreesToMarketing: boolean;
}

const ContactPage: React.FC = () => {
  usePerformanceMonitoring('ContactPage');
  
  // Global floating particles background (mandatory pattern)
  const { particlesRef, containerClasses: particlesContainerClasses, particles } = useFloatingParticles(50);
  
  // Optimized background for contact page
  const { backgroundClasses: contactBackgroundClasses, backgroundStyle: contactBackgroundStyle } = useOptimizedBackground(
    HERO_CONTENT.backgroundImage,
    'linear-gradient(rgba(0, 0, 0, 0.75), rgba(0, 0, 0, 0.9))'
  );
  
  const [formData, setFormData] = useState<FormData>({
    firstName: '',
    lastName: '',
    email: '',
    company: '',
    jobTitle: '',
    subject: '',
    enquiryType: '',
    gpuTypes: [],
    message: '',
    agreesToMarketing: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  // Performance optimizations - containment
  const containmentStyle = useMemo(() => CSSContainment.createContainmentStyle(), []);

  // Contact information elements
  const contactInfoElements = useMemo(() => [
    {
      icon: Phone,
      title: 'Phone Support',
      content: (
        <div>
          <div className="font-medium text-white">{COMPANY_INFO.contact.phone}</div>
          <div className="text-xs text-gray-400 mt-1">Mon-Fri, 9AM-6PM (GMT+8)</div>
        </div>
      ),
      bgColor: 'bg-gradient-to-r from-blue-500 to-blue-600',
      iconColor: 'text-white'
    },
    {
      icon: Mail,
      title: 'Email Support',
      content: (
        <div>
          <div className="font-medium text-white">{COMPANY_INFO.contact.email}</div>
          <div className="text-xs text-gray-400 mt-1">Response within 24 hours</div>
        </div>
      ),
      bgColor: 'bg-gradient-to-r from-emerald-500 to-emerald-600',
      iconColor: 'text-white'
    },
    {
      icon: MapPin,
      title: 'Office Location',
      content: (
        <div>
          <div className="font-medium text-white">Kuala Lumpur</div>
          <div className="text-xs text-gray-400 mt-1">Level 30, South Wing<br />Menara TM, Jalan Pantai Baharu</div>
        </div>
      ),
      bgColor: 'bg-gradient-to-r from-purple-500 to-purple-600',
      iconColor: 'text-white'
    }
  ], []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;

    // Clear validation error for this field when user starts typing
    if (validationErrors[name]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }

    if (type === 'checkbox') {
      const target = e.target as HTMLInputElement;
      if (name === 'gpuTypes') {
        setFormData(prev => ({
          ...prev,
          gpuTypes: target.checked
            ? [...prev.gpuTypes, value]
            : prev.gpuTypes.filter(gpu => gpu !== value)
        }));
      } else {
        setFormData(prev => ({
          ...prev,
          [name]: target.checked
        }));
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const validateForm = (): { isValid: boolean; errors: Record<string, string> } => {
    const errors: Record<string, string> = {};
    
    // First Name validation
    if (!formData.firstName.trim()) {
      errors.firstName = 'Please enter your first name';
    } else if (formData.firstName.trim().length < 2) {
      errors.firstName = 'First name should be at least 2 characters';
    } else if (formData.firstName.trim().length > 50) {
      errors.firstName = 'First name is too long (maximum 50 characters)';
    } else if (!/^[a-zA-Z\s'-]+$/.test(formData.firstName.trim())) {
      errors.firstName = 'Please use only letters and common punctuation';
    }
    
    // Last Name validation
    if (!formData.lastName.trim()) {
      errors.lastName = 'Please enter your last name';
    } else if (formData.lastName.trim().length < 2) {
      errors.lastName = 'Last name should be at least 2 characters';
    } else if (formData.lastName.trim().length > 50) {
      errors.lastName = 'Last name is too long (maximum 50 characters)';
    } else if (!/^[a-zA-Z\s'-]+$/.test(formData.lastName.trim())) {
      errors.lastName = 'Please use only letters and common punctuation';
    }
    
    // Email validation - simplified to avoid double validation
    if (!formData.email.trim()) {
      errors.email = 'Please enter your email address';
    } else if (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(formData.email.trim())) {
      errors.email = 'Please enter a valid email (e.g., name@company.com)';
    } else if (formData.email.length > 100) {
      errors.email = 'Email address is too long (maximum 100 characters)';
    }
    
    // Company validation (optional but with rules if filled)
    if (formData.company.trim()) {
      if (formData.company.trim().length < 2) {
        errors.company = 'Company name should be at least 2 characters';
      } else if (formData.company.trim().length > 100) {
        errors.company = 'Company name is too long (maximum 100 characters)';
      }
    }
    
    // Job Title validation (optional but with rules if filled)
    if (formData.jobTitle.trim()) {
      if (formData.jobTitle.trim().length < 2) {
        errors.jobTitle = 'Job title should be at least 2 characters';
      } else if (formData.jobTitle.trim().length > 100) {
        errors.jobTitle = 'Job title is too long (maximum 100 characters)';
      }
    }
    
    // Subject validation
    if (!formData.subject.trim()) {
      errors.subject = 'Please tell us what this is about';
    } else if (formData.subject.trim().length < 5) {
      errors.subject = 'Subject should be at least 5 characters';
    } else if (formData.subject.trim().length > 200) {
      errors.subject = 'Subject is too long (maximum 200 characters)';
    }
    
    // Enquiry Type validation
    if (!formData.enquiryType) {
      errors.enquiryType = 'Please select what you need help with';
    }
    
    // Message validation
    if (!formData.message.trim()) {
      errors.message = 'Please describe how we can help you';
    } else if (formData.message.trim().length < 10) {
      errors.message = 'Please provide more details (at least 10 characters)';
    } else if (formData.message.trim().length > 5000) {
      errors.message = 'Message is too long (maximum 5000 characters)';
    }
    
    setValidationErrors(errors);
    return { isValid: Object.keys(errors).length === 0, errors };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate form before submitting
    const validation = validateForm();

    if (!validation.isValid) {
      // Scroll to first error field
      const errorFields = Object.keys(validation.errors);
      if (errorFields.length > 0) {
        const firstErrorField = errorFields[0];
        const element = document.getElementById(firstErrorField);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          element.focus();
        }
      }
      return;
    }

    setIsSubmitting(true);

    try {
      // Submit to backend API
      const response = await contactApiService.submitContactForm(formData);

      if (response.success) {
        setIsSubmitting(false);
        setIsSubmitted(true);
        
        // Keep success message visible for 7 seconds before resetting
        setTimeout(() => {
          setIsSubmitted(false);
          setFormData({
            firstName: '',
            lastName: '',
            email: '',
            company: '',
            jobTitle: '',
            subject: '',
            enquiryType: '',
            gpuTypes: [],
            message: '',
            agreesToMarketing: false,
          });
        }, 7000);
      }
    } catch (error: any) {
      // Only show inline error for API/network issues, not validation
      // You could add a toast notification here instead if needed
      setIsSubmitting(false);
    }
  };

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      exit="exit"
      variants={ANIMATION_VARIANTS.fadeIn}
      className="min-h-screen relative"
    >
      {/* Global Floating Particles Background */}
      <div ref={particlesRef} className={`${particlesContainerClasses} fixed inset-0 z-30`}>
        {particles.map((particle) => (
          <div
            key={particle.id}
            className={particle.classes}
            style={particle.style}
          />
        ))}
      </div>

      {/* Page Content */}
      <div className="relative z-40">
        {/* Contact Section */}
        <section className="relative min-h-screen">
          {/* Contact Background */}
          <div 
            className={contactBackgroundClasses}
            style={contactBackgroundStyle}
          />
          
          <div className="container mx-auto px-4 pt-32 w-full relative z-40">
          <motion.div 
            className="max-w-7xl mx-auto relative z-30"
            variants={ANIMATION_VARIANTS.staggerChildren}
            initial="hidden"
            animate="visible"
          >
            {/* Section Header */}
            <motion.div 
              variants={ANIMATION_VARIANTS.slideUp} 
              className="text-center mb-8 pt-28"
              style={containmentStyle}
            >
              <motion.div
                variants={ANIMATION_VARIANTS.slideUp}
                className="flex items-center justify-center mb-6"
              >
                <StyledIcon 
                  icon={Headphones} 
                  size="xl" 
                  color="primary" 
                  variant="static" 
                />
              </motion.div>
              
              <h1 className="heading-1 mb-6">Contact <span className='text-primary-gradient'>MTAI</span></h1>
              <p className="hero-subtitle max-w-3xl mx-auto">
                Ready to take your business to the next level? Contact our experts today 
                for a free consultation and discover how we can help transform your operations.
              </p>
            </motion.div>

            {/* Asymmetric grid layout - form takes 2/3, contact info takes 1/3 */}
            <div className="grid lg:grid-cols-3 gap-6 pt-28 items-stretch">
              {/* Contact Form - Main Content (2 columns) */}
              <motion.div 
                className="lg:col-span-2"
                variants={ANIMATION_VARIANTS.slideInLeft}
                style={containmentStyle}
              >
                <GlassCard className="rounded-3xl p-8 relative overflow-hidden h-full flex flex-col z-30">
                  <div className="relative z-40 flex flex-col h-full">
                    {/* Form Header */}
                    <div className="mb-8">
                      <h2 className="text-3xl font-bold text-white mb-4">
                        Send us a <span className="text-primary-gradient">Message</span>
                      </h2>
                      <p className="text-gray-300">
                        Fill out the form below and our experts will get back to you within 24 hours.
                      </p>
                    </div>

                    {/* Enhanced Form - Growing content area */}
                    <div className="flex-grow">
                      {isSubmitted ? (
                        <motion.div
                          initial={{ scale: 0.8, opacity: 0 }}
                          animate={{ scale: 1, opacity: 1 }}
                          className="text-center py-12"
                          style={containmentStyle}
                        >
                          <div className="w-20 h-20 bg-gradient-to-r from-green-400 to-green-600 rounded-full flex items-center justify-center mx-auto mb-6">
                            <CheckCircle className="w-10 h-10 text-white" />
                          </div>
                          <h3 className="text-2xl font-bold text-white mb-3">
                            Message Sent Successfully!
                          </h3>
                          <p className="text-gray-300 text-lg">
                            Thank you for contacting us. We'll get back to you within 24 hours.
                          </p>
                        </motion.div>
                      ) : (
                        <form onSubmit={handleSubmit} className="space-y-6">
                          {/* Enhanced form fields with better styling */}
                          <div className="grid md:grid-cols-2 gap-6">
                            <div className="group">
                              <label htmlFor="firstName" className="block text-sm font-semibold text-primary-400 mb-3">
                                First Name *
                              </label>
                              <div className="relative">
                                <input
                                  type="text"
                                  id="firstName"
                                  name="firstName"
                                  value={formData.firstName}
                                  onChange={handleInputChange}
                                  className={`w-full px-4 py-4 bg-gray-800/50 border-2 rounded-xl text-white placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-primary-500/50 focus:bg-gray-800/70 transition-all duration-300 group-hover:border-gray-500/50 ${
                                    validationErrors.firstName ? 'border-red-500/50' : 'border-gray-600/30'
                                  }`}
                                  placeholder="Enter your first name"
                                  style={{ willChange: 'transform', transform: 'translateZ(0)' }}
                                />
                                <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-primary-500/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
                              </div>
                              {validationErrors.firstName && (
                                <motion.div 
                                  initial={{ opacity: 0, height: 0 }}
                                  animate={{ opacity: 1, height: 'auto' }}
                                  exit={{ opacity: 0, height: 0 }}
                                  className="flex items-center mt-2 text-sm text-red-400"
                                >
                                  <svg className="w-4 h-4 mr-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                  </svg>
                                  <span>{validationErrors.firstName}</span>
                                </motion.div>
                              )}
                            </div>
                            <div className="group">
                              <label htmlFor="lastName" className="block text-sm font-semibold text-primary-400 mb-3">
                                Last Name *
                              </label>
                              <div className="relative">
                                <input
                                  type="text"
                                  id="lastName"
                                  name="lastName"
                                  value={formData.lastName}
                                  onChange={handleInputChange}
                                  className={`w-full px-4 py-4 bg-gray-800/50 border-2 rounded-xl text-white placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-primary-500/50 focus:bg-gray-800/70 transition-all duration-300 group-hover:border-gray-500/50 ${
                                    validationErrors.lastName ? 'border-red-500/50' : 'border-gray-600/30'
                                  }`}
                                  placeholder="Enter your last name"
                                  style={{ willChange: 'transform', transform: 'translateZ(0)' }}
                                />
                                <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-primary-500/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
                              </div>
                              {validationErrors.lastName && (
                                <div className="flex items-center mt-2 text-sm text-red-400">
                                  <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                  </svg>
                                  <span>{validationErrors.lastName}</span>
                                </div>
                              )}
                            </div>
                          </div>

                          <div className="grid md:grid-cols-2 gap-6">
                            <div className="group">
                              <label htmlFor="company" className="block text-sm font-semibold text-primary-400 mb-3">
                                Company Name
                              </label>
                              <div className="relative">
                                <input
                                  type="text"
                                  id="company"
                                  name="company"
                                  value={formData.company}
                                  onChange={handleInputChange}
                                  className={`w-full px-4 py-4 bg-gray-800/50 border-2 rounded-xl text-white placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-primary-500/50 focus:bg-gray-800/70 transition-all duration-300 group-hover:border-gray-500/50 ${
                                    validationErrors.company ? 'border-red-500/50' : 'border-gray-600/30'
                                  }`}
                                  placeholder="Enter your company name"
                                  maxLength={100}
                                  style={{ willChange: 'transform', transform: 'translateZ(0)' }}
                                />
                                <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-primary-500/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
                              </div>
                              {validationErrors.company && (
                                <div className="flex items-center mt-2 text-sm text-red-400">
                                  <svg className="w-4 h-4 mr-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                  </svg>
                                  <span>{validationErrors.company}</span>
                                </div>
                              )}
                            </div>
                            <div className="group">
                              <label htmlFor="email" className="block text-sm font-semibold text-primary-400 mb-3">
                                Business Email *
                              </label>
                              <div className="relative">
                                <input
                                  type="email"
                                  id="email"
                                  name="email"
                                  value={formData.email}
                                  onChange={handleInputChange}
                                  className={`w-full px-4 py-4 bg-gray-800/50 border-2 rounded-xl text-white placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-primary-500/50 focus:bg-gray-800/70 transition-all duration-300 group-hover:border-gray-500/50 ${
                                    validationErrors.email ? 'border-red-500/50' : 'border-gray-600/30'
                                  }`}
                                  placeholder="Enter your business email"
                                  style={{ willChange: 'transform', transform: 'translateZ(0)' }}
                                />
                                <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-primary-500/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
                              </div>
                              {validationErrors.email && (
                                <div className="flex items-center mt-2 text-sm text-red-400">
                                  <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                  </svg>
                                  <span>{validationErrors.email}</span>
                                </div>
                              )}
                            </div>
                          </div>

                          <div className="grid md:grid-cols-2 gap-6">
                            <div className="group">
                              <label htmlFor="jobTitle" className="block text-sm font-semibold text-primary-400 mb-3">
                                Job Title
                              </label>
                              <div className="relative">
                                <input
                                  type="text"
                                  id="jobTitle"
                                  name="jobTitle"
                                  value={formData.jobTitle}
                                  onChange={handleInputChange}
                                  className={`w-full px-4 py-4 bg-gray-800/50 border-2 rounded-xl text-white placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-primary-500/50 focus:bg-gray-800/70 transition-all duration-300 group-hover:border-gray-500/50 ${
                                    validationErrors.jobTitle ? 'border-red-500/50' : 'border-gray-600/30'
                                  }`}
                                  placeholder="Enter your job title"
                                  maxLength={100}
                                  style={{ willChange: 'transform', transform: 'translateZ(0)' }}
                                />
                                <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-primary-500/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
                              </div>
                              {validationErrors.jobTitle && (
                                <div className="flex items-center mt-2 text-sm text-red-400">
                                  <svg className="w-4 h-4 mr-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                  </svg>
                                  <span>{validationErrors.jobTitle}</span>
                                </div>
              )}
                            </div>
                            <div className="group">
                              <label htmlFor="subject" className="block text-sm font-semibold text-primary-400 mb-3">
                                Subject *
                              </label>
                              <div className="relative">
                                <input
                                  type="text"
                                  id="subject"
                                  name="subject"
                                  value={formData.subject}
                                  onChange={handleInputChange}
                                  className={`w-full px-4 py-4 bg-gray-800/50 border-2 rounded-xl text-white placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-primary-500/50 focus:bg-gray-800/70 transition-all duration-300 group-hover:border-gray-500/50 ${
                                    validationErrors.subject ? 'border-red-500/50' : 'border-gray-600/30'
                                  }`}
                                  placeholder="Enter your subject"
                                  style={{ willChange: 'transform', transform: 'translateZ(0)' }}
                                />
                                <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-primary-500/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
                              </div>
                              {validationErrors.subject && (
                                <div className="flex items-center mt-2 text-sm text-red-400">
                                  <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                  </svg>
                                  <span>{validationErrors.subject}</span>
                                </div>
                              )}
                            </div>
                          </div>

                          <div className="group">
                            <label htmlFor="enquiryType" className="block text-sm font-semibold text-primary-400 mb-3">
                              I am enquiring about:
                            </label>
                            <div className="relative">
                              <select
                                id="enquiryType"
                                name="enquiryType"
                                value={formData.enquiryType}
                                onChange={handleInputChange}
                                className={`w-full px-4 py-4 bg-gray-800/50 border-2 rounded-xl text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500/50 focus:bg-gray-800/70 transition-all duration-300 group-hover:border-gray-500/50 appearance-none cursor-pointer ${
                                  validationErrors.enquiryType ? 'border-red-500/50' : 'border-gray-600/30'
                                }`}
                                style={{ willChange: 'transform', transform: 'translateZ(0)' }}
                              >
                                <option value="" className="bg-gray-800 text-gray-400">- Select an option -</option>
                                <option value="GPU Computing Solutions" className="bg-gray-800 text-white">GPU Computing Solutions</option>
                                <option value="AI Software Development" className="bg-gray-800 text-white">AI Software Development</option>
                                <option value="Custom Software Solutions" className="bg-gray-800 text-white">Custom Software Solutions</option>
                                <option value="Partnership Opportunities" className="bg-gray-800 text-white">Partnership Opportunities</option>
                                <option value="Technical Support" className="bg-gray-800 text-white">Technical Support</option>
                                <option value="General Inquiry" className="bg-gray-800 text-white">General Inquiry</option>
                              </select>
                              <div className="absolute inset-y-0 right-4 flex items-center pointer-events-none">
                                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                              </div>
                            </div>
                            {validationErrors.enquiryType && (
                              <div className="flex items-center mt-2 text-sm text-red-400">
                                <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                </svg>
                                <span>{validationErrors.enquiryType}</span>
                              </div>
                            )}
                          </div>

                          <div>
                            <label className="block text-sm font-semibold text-primary-400 mb-4">
                              Types of GPU:
                            </label>
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                              {[
                                'Nvidia RTX4090',
                                'Nvidia L40',
                                'Nvidia L40S',
                                'Nvidia HGX H100',
                                'Nvidia HGX H200',
                                'Nvidia HGX B200'
                              ].map((gpu) => (
                                <label key={gpu} className="flex items-center space-x-3 cursor-pointer group p-3 rounded-lg hover:bg-gray-800/30 transition-colors">
                                  <input
                                    type="checkbox"
                                    name="gpuTypes"
                                    value={gpu}
                                    checked={formData.gpuTypes.includes(gpu)}
                                    onChange={handleInputChange}
                                    className="w-4 h-4 bg-gray-700/50 border-2 border-gray-600/50 rounded focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-primary-500 transition-colors"
                                  />
                                  <span className="text-sm text-gray-300 group-hover:text-white transition-colors font-medium">
                                    {gpu}
                                  </span>
                                </label>
                              ))}
                            </div>
                          </div>

                          <div className="group">
                            <label htmlFor="message" className="block text-sm font-semibold text-primary-400 mb-3 flex justify-between items-center">
                              <span>Message *</span>
                              <span className="text-xs text-gray-400 font-normal">
                                {formData.message.length}/5000 characters
                              </span>
                            </label>
                            <div className="relative">
                              <textarea
                                id="message"
                                name="message"
                                value={formData.message}
                                onChange={handleInputChange}
                                rows={5}
                                className={`w-full px-4 py-4 bg-gray-800/50 border-2 rounded-xl text-white placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-primary-500/50 focus:bg-gray-800/70 transition-all duration-300 resize-vertical group-hover:border-gray-500/50 ${
                                  validationErrors.message ? 'border-red-500/50' : 'border-gray-600/30'
                                }`}
                                placeholder="Describe your high-performance computing needs and business objectives... (minimum 10 characters)"
                                maxLength={5000}
                                style={{ willChange: 'transform', transform: 'translateZ(0)' }}
                              />
                              <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-primary-500/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
                            </div>
                            {validationErrors.message && (
                              <div className="flex items-center mt-2 text-sm text-red-400">
                                <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                </svg>
                                <span>{validationErrors.message}</span>
                              </div>
                            )}
                          </div>

                          <div className="text-sm text-gray-300 p-4 bg-gray-800/30 rounded-lg border border-gray-700/50">
                            By submitting this contact form, you agree to our privacy practices and 
                            guidelines as listed in the <Link to="/privacy" className="text-primary-400 hover:text-primary-300 underline" onClick={handlePageNavigation}>Privacy Policy</Link>.
                          </div>

                          <div className="flex items-start space-x-3 p-4 bg-gray-800/20 rounded-lg">
                            <input
                              type="checkbox"
                              id="agreesToMarketing"
                              name="agreesToMarketing"
                              checked={formData.agreesToMarketing}
                              onChange={handleInputChange}
                              className="w-5 h-5 mt-0.5 bg-gray-700/50 border-2 border-gray-600/50 rounded focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-primary-500"
                            />
                            <label htmlFor="agreesToMarketing" className="text-sm text-gray-300 cursor-pointer leading-relaxed">
                              I agree to receive marketing communications from MTAI about new products, services, and industry insights.
                            </label>
                          </div>

                          {/* Submit Button - Inside form */}
                          <div className="mt-8 pt-6 border-t border-gray-700/50">
                            <button
                              type="submit"
                              disabled={isSubmitting}
                              className="w-full btn-futuristic disabled:opacity-50 disabled:cursor-not-allowed text-lg py-4"
                            >
                              {isSubmitting ? (
                                <div className="flex items-center justify-center">
                                  <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin mr-3" />
                                  Sending Message...
                                </div>
                              ) : (
                                <div className="flex items-center justify-center">
                                  <Send className="w-6 h-6 mr-3" />
                                  Submit Form
                                </div>
                              )}
                            </button>
                          </div>
                        </form>
                      )}
                    </div>
                  </div>
                </GlassCard>
              </motion.div>

              {/* Contact Information Sidebar - Compact (1 column) */}
              <motion.div 
                className="lg:col-span-1 relative z-30"
                variants={ANIMATION_VARIANTS.slideInRight}
                style={containmentStyle}
              >
                <div className="flex flex-col h-full space-y-6">
                  {/* Contact Information Header */}
                  <GlassCard className="rounded-2xl p-6 relative z-40">
                    <h2 className="text-xl font-bold text-white mb-1">
                      Contact <span className="text-primary-gradient">Information</span>
                    </h2>
                    <p className="text-gray-400 text-sm mb-6">
                      Get in touch with our experts
                    </p>
                    
                    <div className="space-y-4">
                      {contactInfoElements.map((item, index) => (
                        <div key={index} className="group">
                          <div className="flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-800/30 transition-colors">
                            <StyledIcon 
                              icon={item.icon}
                              size="xs"
                              color="primary"
                              variant="default"
                            />
                            <div className="flex-1 min-w-0">
                              <h3 className="font-semibold text-white text-sm mb-1">{item.title}</h3>
                              <div className="text-gray-300 text-xs leading-relaxed">
                                {item.content}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </GlassCard>

                  {/* Response Time Card */}
                  <GlassCard className="rounded-2xl p-6 relative z-40">
                    <div className="flex items-center gap-3 mb-4">
                      <StyledIcon 
                        icon={Clock}
                        size="xs"
                        color="green"
                        variant="default"
                      />
                      <div>
                        <div className="text-white font-semibold text-sm">Quick Response</div>
                        <div className="text-gray-400 text-xs">We respond fast</div>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="text-center p-3 bg-gray-800/30 rounded-lg">
                        <div className="text-lg font-bold text-primary-400">&lt; 24h</div>
                        <div className="text-xs text-gray-400">Response Time</div>
                      </div>
                      <div className="text-center p-3 bg-gray-800/30 rounded-lg">
                        <div className="text-lg font-bold text-primary-400">24/7</div>
                        <div className="text-xs text-gray-400">Support</div>
                      </div>
                    </div>
                  </GlassCard>

                  {/* Google Maps Display - Compact */}
                  <div className="flex-grow">
                    <GlassCard className="rounded-2xl overflow-hidden h-full flex flex-col relative z-40">
                      <div className="p-4 pb-0">
                        <h3 className="text-white font-semibold text-sm mb-2">Our Location</h3>
                        <p className="text-gray-400 text-xs mb-4">Visit our office in Kuala Lumpur</p>
                      </div>
                      <div className="flex-grow">
                        <iframe
                          src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3983.918600020806!2d101.6653853!3d3.1162357999999997!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x31cc4985e66b5031%3A0x33e1df25b470d7ce!2sMTAI%20(M%20Telecommunications%20Sdn%20Bhd)!5e0!3m2!1sen!2smy!4v1754366767886!5m2!1sen!2smy"
                          width="100%"
                          height="200"
                          style={{ border: 0 }}
                          allowFullScreen
                          loading="lazy"
                          referrerPolicy="no-referrer-when-downgrade"
                          title="MTAI Office Location - Menara TM"
                          className="w-full h-full min-h-[200px]"
                        />
                      </div>
                      <div className="p-4 bg-gray-800/50">
                        <div className="flex items-center space-x-2">
                          <MapPin className="w-4 h-4 text-primary-400 flex-shrink-0" />
                          <p className="text-xs text-gray-300">
                            Level 30, South Wing, Menara TM - Click map for directions
                          </p>
                        </div>
                      </div>
                    </GlassCard>
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
        </section>
      </div>
    </motion.div>
  );
};

export default ContactPage;
